import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { PRE_CALL_SMS_DEFAULT_MINUTES, PRE_CALL_SMS_FEATURE_ID } from '~/consts/preCallSms';

import { useSendSms } from '~/hooks/apis/notification-proxy/useSendSms';
import useCurrentUser from '~/hooks/useCurrentUser';

import { trackCsAgentFeatureUsed } from '~/services/snowplow/trackCsAgentFeatureUsed';

import getErrorMessage from '~/utils/getErrorMessage';
import {
  buildCustomerSmsPhoneNumber,
  buildPreCallSmsMessage,
  describePreCallSmsSupportedCountries,
  isPreCallSmsSupportedCountry,
  resolvePreCallSmsAgentName,
  resolvePreCallSmsCustomerFirstName,
} from '~/utils/preCallSms';

export type PreCallSmsCustomer = {
  givenName?: string | null;
  phone_prefix?: string | null;
  phone?: string | null;
};

interface Props {
  open: boolean;
  onClose: () => void;
  customer: PreCallSmsCustomer;
}

export default function SendPreCallSmsDialog({ open, onClose, customer }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();
  const tenantTitle = useSelector((state: App.State) => state.tenant.title);
  const sendSmsMutation = useSendSms();

  const phoneNumber = useMemo(
    () => buildCustomerSmsPhoneNumber(customer.phone_prefix, customer.phone),
    [customer.phone, customer.phone_prefix],
  );

  const defaultMessage = useMemo(
    () =>
      buildPreCallSmsMessage({
        customerFirstName: resolvePreCallSmsCustomerFirstName(customer.givenName),
        agentName: resolvePreCallSmsAgentName(currentUser),
        brandName: tenantTitle,
      }),
    [customer.givenName, currentUser, tenantTitle],
  );

  const [message, setMessage] = useState(defaultMessage);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMessage(defaultMessage);
      setErrorMessage(null);
    }
  }, [defaultMessage, open]);

  const isSupportedCountry = isPreCallSmsSupportedCountry(customer.phone_prefix);
  const supportedCountries = describePreCallSmsSupportedCountries();

  const trimmedMessage = message.trim();
  const canSend = Boolean(phoneNumber && isSupportedCountry && trimmedMessage && !sendSmsMutation.isPending);
  const isDefaultMessage = trimmedMessage === defaultMessage.trim();

  const handleClose = () => {
    if (sendSmsMutation.isPending) {
      return;
    }
    onClose();
  };

  const handleSendSms = async () => {
    if (!phoneNumber) {
      setErrorMessage('This customer has no mobile number on file.');
      return;
    }

    if (!isSupportedCountry) {
      setErrorMessage(`Pre-call SMS is available for ${supportedCountries} phone numbers only.`);
      return;
    }

    if (!trimmedMessage) {
      setErrorMessage('Enter the SMS message before sending.');
      return;
    }

    setErrorMessage(null);

    try {
      await sendSmsMutation.mutateAsync({
        phoneNumber,
        message: trimmedMessage,
      });

      const agentId = currentUser?.id_member ?? currentUser?.memberId;
      if (agentId && currentUser?.email) {
        trackCsAgentFeatureUsed(PRE_CALL_SMS_FEATURE_ID, { id: agentId, email: currentUser.email });
      }

      enqueueSnackbar('Pre-call SMS sent successfully', { variant: 'success' });
      onClose();
    } catch (error) {
      const failureMessage = getErrorMessage(error, 'Failed to send pre-call SMS.');
      setErrorMessage(failureMessage);
      enqueueSnackbar(failureMessage, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Send pre-call SMS</DialogTitle>
      {errorMessage && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }}>
          {errorMessage}
        </Alert>
      )}
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!phoneNumber && <Alert severity="warning">This customer has no mobile number on file.</Alert>}
          {phoneNumber && !isSupportedCountry && (
            <Alert severity="warning">
              Pre-call SMS is available for {supportedCountries} phone numbers only. This customer&apos;s number is{' '}
              <strong>{phoneNumber}</strong>.
            </Alert>
          )}
          {phoneNumber && isSupportedCountry && (
            <Alert severity="info">
              SMS will be sent to <strong>{phoneNumber}</strong> (from the customer profile).
            </Alert>
          )}
          {isDefaultMessage && isSupportedCountry && (
            <Alert severity="warning">
              This message currently says the call is in around {PRE_CALL_SMS_DEFAULT_MINUTES} minutes. You can change
              that wait time if you want before sending.
            </Alert>
          )}
          <TextField
            label="SMS preview"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            multiline
            minRows={4}
            required
            fullWidth
            disabled={sendSmsMutation.isPending}
            error={!trimmedMessage}
            helperText={!trimmedMessage ? 'Message is required' : 'This is the exact SMS that will be sent.'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text" disabled={sendSmsMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSendSms}
          variant="contained"
          color="primary"
          disabled={!canSend}
          loading={sendSmsMutation.isPending}
        >
          Send SMS
        </Button>
      </DialogActions>
    </Dialog>
  );
}
